/**
 * Who is in which project (project scope slice 3): the `contains` links the
 * Projects view and the Graph already write (`project:<id> —contains→
 * board:<id>` / `document:<id>` / `ssh_host:<vaultId>` …). Nothing is stamped
 * on the items themselves, so a board's synced record does not change and a
 * team project keeps projectsStore's team checks (`addToProject`).
 *
 *   memberIds    node ids the ACTIVE project contains
 *   assignedIds  node ids ANY known project contains ("No project" = not in here)
 *
 * The hook hands `itemInProject` (projectScopeFilter) its whole scope argument.
 */
import { useMemo } from 'react';
import { useProjectScopeStore } from './projectScopeStore';

const EMPTY = Object.freeze(new Set());

/** Pure: the two sets for `activeProjectId` over `links` (only projects in `projects` count). */
export function membershipOf(links = [], activeProjectId = null, projects = null) {
  const known = projects ? new Set(projects.map((p) => `project:${p.id}`)) : null;
  const active = activeProjectId ? `project:${activeProjectId}` : null;
  const memberIds = new Set();
  const assignedIds = new Set();
  for (const l of links || []) {
    if (!l || l.rel !== 'contains') continue;
    const from = String(l.from);
    if (known && !known.has(from)) continue;
    assignedIds.add(String(l.to));
    if (from === active) memberIds.add(String(l.to));
  }
  return { memberIds, assignedIds };
}

/** The filter scope right now, outside React (the DB Manager bridge). */
export function currentFilterScope() {
  const s = useProjectScopeStore.getState();
  const { memberIds, assignedIds } = membershipOf(s.known.links, s.activeProjectId, s.known.projects);
  return { activeProjectId: s.activeProjectId, activeProject: s.activeProject, status: s.status, memberIds, assignedIds };
}

/** `{ activeProjectId, activeProject, status, memberIds, assignedIds }` — the filter scope. */
export function useProjectFilterScope() {
  const activeProjectId = useProjectScopeStore((s) => s.activeProjectId);
  const activeProject = useProjectScopeStore((s) => s.activeProject);
  const status = useProjectScopeStore((s) => s.status);
  const links = useProjectScopeStore((s) => s.known.links);
  const projects = useProjectScopeStore((s) => s.known.projects);
  return useMemo(() => {
    const { memberIds, assignedIds } = links?.length ? membershipOf(links, activeProjectId, projects) : { memberIds: EMPTY, assignedIds: EMPTY };
    return { activeProjectId, activeProject, status, memberIds, assignedIds };
  }, [activeProjectId, activeProject, status, links, projects]);
}
